export const listSlidesFiles = async (accessToken) => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=mimeType='application/vnd.google-apps.presentation' and trashed=false&corpus=user&includeItemsFromAllDrives=true&supportsAllDrives=true&fields=files(id,name,owners,ownedByMe)`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json"
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return data.files || [];
  } catch (error) {
    console.error("Error fetching Google Slides:", error);
    return [];
  }
};

/** Google Slides files whose parent is the given pack folder (folder id = pack id). */
export const listSlidesFilesInFolder = async (accessToken, folderId) => {
  const all = [];
  let pageToken = null;
  const q = `'${folderId}' in parents and mimeType='application/vnd.google-apps.presentation' and trashed=false`;
  try {
    do {
      const params = new URLSearchParams({
        q,
        fields: "nextPageToken, files(id,name,owners,ownedByMe)",
        pageSize: "100",
        includeItemsFromAllDrives: "true",
        supportsAllDrives: "true",
      });
      if (pageToken) params.set("pageToken", pageToken);
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
        }
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.setItem("deckpack_auth_expired", Date.now().toString());
          return [];
        }
        const errorText = await response.text();
        throw new Error(`Error: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      all.push(...(data.files || []));
      pageToken = data.nextPageToken || null;
    } while (pageToken);
    return all;
  } catch (error) {
    console.error("Error listing Google Slides in folder:", error);
    return [];
  }
};

/** Minimal Drive file fields for playlist add / shared-deck prompts. */
export async function getDriveFileBasic(accessToken, fileId) {
  if (!accessToken || !fileId) return null;
  try {
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=id,name,ownedByMe,mimeType&supportsAllDrives=true`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.warn('getDriveFileBasic', e);
    return null;
  }
}

export const getCurrentSlide = async (accessToken, presentationId) => {
  try {
    const response = await fetch(
      `https://slides.googleapis.com/v1/presentations/${presentationId}?fields=presentationId,slides(objectId,pageElements)`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        localStorage.setItem('deckpack_auth_expired', Date.now().toString());
        return { slides: [] };
      }
      throw new Error(`Error: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching current slide:", error);
    return null;
  }
};

export const getCurrentSlidePosition = async (accessToken, presentationId) => {
  try {
    const response = await fetch(
      `https://slides.googleapis.com/v1/presentations/${presentationId}?fields=slideProperties.currentPage`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      }
    );

    if (!response.ok) throw new Error(`Error: ${response.status}`);
    const data = await response.json();
    return data.slideProperties?.currentPage;
  } catch (error) {
    console.error("Error getting current slide:", error);
    return null;
  }
};

// Thumbnail cache and rate limiting
// contentUrl from Slides API expires in ~30 min; cache must be shorter
const THUMBNAIL_URL_TTL_MS = 25 * 60 * 1000; // 25 minutes

/** Abort hung browser fetches so thumbnail / Slides loading cannot stall the UI forever. */
const SLIDES_PRESENTATION_FETCH_TIMEOUT_MS = 45_000;
const THUMBNAIL_FETCH_TIMEOUT_MS = 25_000;
/** Stop infinite 429 retry loops on the global thumbnail queue. */
const THUMBNAIL_MAX_429_RETRIES = 12;

function fetchWithTimeout(url, init = {}, timeoutMs = 30_000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}

/** Default Slides API thumbnail size. MEDIUM avoids blurry upscaling in the UI; SMALL is too soft. */
const DEFAULT_SLIDE_THUMBNAIL_SIZE = 'MEDIUM';
const thumbnailCache = new Map();
const thumbnailRequestQueue = [];
let isProcessingQueue = false;
const REQUEST_DELAY = 100; // 100ms between requests (10 requests/second max)

const processThumbnailQueue = async () => {
  if (isProcessingQueue || thumbnailRequestQueue.length === 0) return;
  
  isProcessingQueue = true;
  
  while (thumbnailRequestQueue.length > 0) {
    const item = thumbnailRequestQueue.shift();
    const { resolve, reject, url, headers, retries = 0, retries429 = 0 } = item;
    
    try {
      // Check cache first (TTL must be under API's ~30 min URL expiry)
      const cacheKey = url;
      const cached = thumbnailCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < THUMBNAIL_URL_TTL_MS) {
        resolve(cached.data);
        continue;
      }
      
      const response = await fetchWithTimeout(url, { headers }, THUMBNAIL_FETCH_TIMEOUT_MS);
      
      if (response.status === 429) {
        if (retries429 >= THUMBNAIL_MAX_429_RETRIES) {
          throw new Error('Too many 429 responses for thumbnail');
        }
        thumbnailRequestQueue.unshift({ ...item, retries429: retries429 + 1 });
        await new Promise(r => setTimeout(r, 2000));
        continue;
      }
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const data = await response.json();
      thumbnailCache.set(cacheKey, { data, timestamp: Date.now() });
      resolve(data);
    } catch (error) {
      if (retries < 1) {
        thumbnailRequestQueue.unshift({ ...item, retries: retries + 1 });
        await new Promise(r => setTimeout(r, REQUEST_DELAY));
      } else {
        reject(error);
      }
    }
    
    if (thumbnailRequestQueue.length > 0) {
      await new Promise(r => setTimeout(r, REQUEST_DELAY));
    }
  }
  
  isProcessingQueue = false;
};

const fetchThumbnailWithRateLimit = (url, headers) => {
  return new Promise((resolve, reject) => {
    thumbnailRequestQueue.push({ resolve, reject, url, headers, retries: 0 });
    processThumbnailQueue();
  });
};

function readSizedSlideCache(presentationId, size) {
  const key = `slide-content-${presentationId}-${size}`;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.data?.slides &&
      parsed.timestamp &&
      Date.now() - parsed.timestamp < THUMBNAIL_URL_TTL_MS
    ) {
      return parsed.data;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

/**
 * Read cached slide data (same keys as getSlideContent). Prefers MEDIUM, then SMALL (legacy), then App’s slide-content-{id}.
 * @param {string} presentationId
 * @returns {{ slides: Array } | null}
 */
export function readSlideContentFromLocalStorage(presentationId) {
  if (!presentationId) return null;

  const medium = readSizedSlideCache(presentationId, 'MEDIUM');
  if (medium) return medium;

  const small = readSizedSlideCache(presentationId, 'SMALL');
  if (small) return small;

  const legacyKey = `slide-content-${presentationId}`;
  try {
    const rawLegacy = localStorage.getItem(legacyKey);
    if (rawLegacy) {
      const parsed = JSON.parse(rawLegacy);
      if (parsed?.slides && Array.isArray(parsed.slides)) {
        return parsed;
      }
    }
  } catch (e) {
    // ignore
  }

  return null;
}

/** thumbnailSize: 'SMALL', 'MEDIUM' (default), or 'LARGE'. Default MEDIUM keeps thumbnails sharp in the UI. */
export const getSlideContent = async (accessToken, presentationId, options = {}) => {
  const thumbnailSize = options.thumbnailSize || DEFAULT_SLIDE_THUMBNAIL_SIZE;
  const skipCache = options.skipCache || false;
  try {
    const cacheKey = `slide-content-${presentationId}-${thumbnailSize}`;
    if (!skipCache) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.timestamp && Date.now() - parsed.timestamp < THUMBNAIL_URL_TTL_MS) {
            return parsed.data;
          }
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }
    }

    const response = await fetchWithTimeout(
      `https://slides.googleapis.com/v1/presentations/${presentationId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
      SLIDES_PRESENTATION_FETCH_TIMEOUT_MS
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        localStorage.setItem('deckpack_auth_expired', Date.now().toString());
        return { slides: [] };
      }
      throw new Error(`Error: ${response.status}`);
    }
    const data = await response.json();

    const slides = await Promise.all(data.slides.map(async (slide, index) => {
      // Use rate-limited thumbnail fetching; if one fails, still return the slide
      let thumbnail = null;
      try {
        const thumbnailUrl = `https://slides.googleapis.com/v1/presentations/${presentationId}/pages/${slide.objectId}/thumbnail?thumbnailProperties.thumbnailSize=${thumbnailSize}`;
        const thumbnailData = await fetchThumbnailWithRateLimit(thumbnailUrl, {
            Authorization: `Bearer ${accessToken}`,
        });
        thumbnail = thumbnailData?.contentUrl || null;
      } catch (err) {
        console.warn(`Thumbnail failed for slide ${index + 1}:`, err.message || err);
        }

      // Extract rich speaker notes and convert them to HTML so we preserve
      // bullets, numbering, bold/italic/underline, links, colors, and indents.
      const notesElement = slide.slideProperties?.notesPage?.pageElements
        ?.find(element => element.shape?.shapeType === 'TEXT_BOX' && element.shape?.text?.textElements);

      const textElements = notesElement?.shape?.text?.textElements || [];

      const escapeHtml = (str) =>
        str
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

      const styleTextRun = (content, textStyle = {}) => {
        let html = escapeHtml(content).replace(/\n/g, "<br/>");

        if (!html.trim()) return "";

        // Text color
        const rgb = textStyle.foregroundColor?.opaqueColor?.rgbColor;
        if (rgb) {
          const r = Math.round((rgb.red ?? 0) * 255);
          const g = Math.round((rgb.green ?? 0) * 255);
          const b = Math.round((rgb.blue ?? 0) * 255);
          html = `<span style="color: rgb(${r}, ${g}, ${b});">${html}</span>`;
        }

        // Links
        if (textStyle.link?.url) {
          const url = escapeHtml(textStyle.link.url);
          html = `<a href="${url}" target="_blank" rel="noreferrer">${html}</a>`;
        }

        // Underline / italic / bold – ordered so tags nest nicely
        if (textStyle.underline) {
          html = `<u>${html}</u>`;
        }
        if (textStyle.italic) {
          html = `<em>${html}</em>`;
        }
        if (textStyle.bold) {
          html = `<strong>${html}</strong>`;
        }

        return html;
      };

      const notesHtmlFromTextElements = (elements) => {
        if (!elements.length) return "";

        let html = "";
        let buffer = "";
        let currentBulletPrefix = "";
        let currentParagraphTextStyle = null;

        const closeParagraphBuffer = () => {
          if (!buffer && !currentBulletPrefix) return;
          const content = `${currentBulletPrefix}${buffer}`.trimEnd();
          if (content) {
            html += `<p>${content}</p>`;
          }
          buffer = "";
          currentBulletPrefix = "";
        };

        const lists = data.lists || {};

        for (const el of elements) {
          // Paragraph markers define bullets / numbering
          if (el.paragraphMarker) {
            // Finish previous paragraph before starting a new one
            closeParagraphBuffer();

            const bullet = el.paragraphMarker.bullet;
            // Paragraph-level text style (used as a fallback when runs don't
            // carry explicit styles).
            currentParagraphTextStyle = el.paragraphMarker.style?.defaultTextStyle || null;

            if (bullet) {
              // Determine a visible prefix for the bullet / number so that
              // the notes view matches the structure the user sees in Slides.
              const list = bullet.listId && lists ? lists[bullet.listId] : null;
              const nestingLevel = bullet.nestingLevel || 0;
              const glyphType =
                list?.listProperties?.nestingLevels?.[nestingLevel]?.glyphType || "";
              const glyphSymbol = (bullet.glyphSymbol || "").trim();
              const upperGlyphType = (glyphType || "").toUpperCase();

              // If Slides gives us an explicit symbol (e.g. "1.", "2)"), use it.
              // Otherwise fall back to a simple bullet that approximates
              // the appearance.
              if (glyphSymbol) {
                currentBulletPrefix = `${escapeHtml(glyphSymbol)} `;
              } else if (upperGlyphType && !upperGlyphType.includes("BULLET")) {
                // Numbered list where we don't have the actual symbol; approximate.
                currentBulletPrefix = "1. ";
              } else {
                // Plain bullet
                currentBulletPrefix = "• ";
              }
            } else {
              currentBulletPrefix = "";
            }
          }

          if (el.textRun && el.textRun.content) {
            // Merge paragraph-level default text style (if any) with the
            // textRun's own style so that bold/italic/underline/colors coming
            // from the paragraph are still respected.
            const effectiveStyle = {
              ...(currentParagraphTextStyle || {}),
              ...(el.textRun.textStyle || {})
            };
            const piece = styleTextRun(el.textRun.content, effectiveStyle);
            buffer += piece;
          }
        }

        // Flush any remaining text
        closeParagraphBuffer();

        return html.trim();
      };

      const notesHtml = notesHtmlFromTextElements(textElements);

      return {
        objectId: slide.objectId,
        thumbnail,
        number: index + 1,
        // Notes are HTML so the UI can render bullets, numbering, bold,
        // italics, links, colors, and indentation.
        notes: notesHtml
      };
    }));

    const result = { slides };
    
    // Cache the result
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data: result,
        timestamp: Date.now()
      }));
    } catch (e) {
      // localStorage might be full, ignore
    }

    return result;
  } catch (error) {
    console.error("Error fetching slide content:", error);
    return { slides: [] };
  }
};